import {SidebarProvider, SidebarInset} from "@/components/ui/sidebar";
import {AppSidebar} from "@/components/app-sidebar";
import {SiteHeader} from "@/components/site-header";
import React, {useState, useEffect} from "react";
import {Outlet, useOutletContext, useNavigate, useLocation} from "react-router-dom";
import axiosInstance from "@/api/axios";

// Helper functions for sessionStorage
const getStoredState = (key, defaultValue) => {
    try {
        const storedValue = sessionStorage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
        console.error(`Error retrieving ${key} from sessionStorage:`, error);
        return defaultValue;
    }
};

const setStoredState = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error(`Error storing ${key} in sessionStorage:`, error);
    }
};

const SidebarLayout = () => {
    // State for time range and bin selection
    const [timeRangeDropdownOpen, setTimeRangeDropdownOpen] = useState(false);
    const [binDropdownOpen, setBinDropdownOpen] = useState(false);
    const [userRole, setUserRole] = useState("Basic");
    const navigate = useNavigate();
    const location = useLocation();

    // Define time range options
    const timeRangeOptions = ["Day", "Week", "Month", "3 Months", "6 Months", "Year"];

    // State for selected time range and bins - use sessionStorage for persistence
    const [selectedTimeRange, setSelectedTimeRange] = useState(
        () => getStoredState('selectedTimeRange', timeRangeOptions[2])
    );
    const [selectedBins, setSelectedBins] = useState(
        () => getStoredState('selectedBins', [])
    );
    const [allBins, setAllBins] = useState([]);

    // State for year selection - use sessionStorage for persistence
    const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(
        () => getStoredState('selectedYear', new Date().getFullYear().toString())
    );
    const yearOptions = Array.from({length: 5}, (_, i) => (new Date().getFullYear() - i).toString());

    // Revert waste type options to match original values used in components
    const wasteTypeOptions = ["pmd", "organic", "paper", "residual"];

    // State for waste type selection - use sessionStorage for persistence
    const [wasteTypeDropdownOpen, setWasteTypeDropdownOpen] = useState(false);
    const [selectedWasteTypes, setSelectedWasteTypes] = useState(
        () => getStoredState('selectedWasteTypes', [])
    );

    // State for cost savings page - use sessionStorage for persistence
    const [selectedWaste, setSelectedWaste] = useState(
        () => getStoredState('selectedWaste', 'pmd')
    );

    // NEW: State for custom date range - use sessionStorage for persistence
    const [customDateRange, setCustomDateRange] = useState(
        () => getStoredState('customDateRange', [{
            startDate: null,
            endDate: null,
            key: 'selection'
        }])
    );
    const [isCustomDateRange, setIsCustomDateRange] = useState(
        () => getStoredState('isCustomDateRange', false)
    );

    // Update sessionStorage when states change
    useEffect(() => {
        setStoredState('selectedTimeRange', selectedTimeRange);
    }, [selectedTimeRange]);

    useEffect(() => {
        setStoredState('selectedBins', selectedBins);
    }, [selectedBins]);

    useEffect(() => {
        setStoredState('selectedYear', selectedYear);
    }, [selectedYear]);

    useEffect(() => {
        setStoredState('selectedWasteTypes', selectedWasteTypes);
    }, [selectedWasteTypes]);

    useEffect(() => {
        setStoredState('selectedWaste', selectedWaste);
    }, [selectedWaste]);

    // NEW: Update sessionStorage for custom date range
    useEffect(() => {
        setStoredState('customDateRange', customDateRange);
    }, [customDateRange]);

    useEffect(() => {
        setStoredState('isCustomDateRange', isCustomDateRange);
    }, [isCustomDateRange]);

    // Fetch user role
    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const response = await axiosInstance.get("usertier");
                console.log("role: ", response.data.tier);
                setUserRole(response.data.tier);

                if (response.data === "Basic") {
                    if (location.pathname !== '/operations') {
                        navigate('/operations');
                    }
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };

        fetchUserRole();
    }, [location.pathname, navigate]);

    // Fetch bin IDs dynamically
    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                console.log("Layout: Fetching bin IDs from API");
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                console.log("Layout: API returned bin IDs:", binIds);
                console.log("Layout: Number of bins returned:", binIds.length);

                // Validate bin IDs - ensure they're properly formatted
                const validBinIds = binIds.filter(id => id && typeof id === 'string');
                if (validBinIds.length !== binIds.length) {
                    console.warn(`Layout: Filtered out ${binIds.length - validBinIds.length} invalid bin IDs`);
                }

                // Always update allBins with fresh data from API
                setAllBins(validBinIds);
                console.log("Layout: Updated allBins with", validBinIds.length, "bins");

                // Get stored bin selection
                const storedBins = getStoredState('selectedBins', []);
                console.log("Layout: Retrieved stored bins:", storedBins.length, "bins");

                // If we have stored bins, validate them against current available bins
                if (storedBins.length > 0) {
                    // Only keep stored bins that exist in the current valid bins
                    const validStoredBins = storedBins.filter(bin => validBinIds.includes(bin));
                    console.log("Layout: Valid stored bins:", validStoredBins.length, "bins");

                    if (validStoredBins.length > 0) {
                        // Use valid stored bins, but limit to first 3 if there are too many
                        const finalSelectedBins = validStoredBins.slice(0, Math.min(3, validStoredBins.length));
                        console.log("Layout: Using final selection of", finalSelectedBins.length, "bins");
                        setSelectedBins(finalSelectedBins);
                    } else {
                        // If no valid stored bins, select first 3 from available bins
                        const initialBins = validBinIds.slice(0, Math.min(3, validBinIds.length));
                        console.log("Layout: No valid stored bins, selecting first", initialBins.length, "bins");
                        setSelectedBins(initialBins);
                    }
                } else {
                    // No stored bins, select first 3 from available bins
                    const initialBins = validBinIds.slice(0, Math.min(3, validBinIds.length));
                    console.log("Layout: No stored bins, selecting first", initialBins.length, "bins");
                    setSelectedBins(initialBins);
                }
            } catch (error) {
                console.error("Layout: Error fetching bin IDs:", error);
            }
        };

        fetchBinIds();
    }, []); // No dependencies to avoid re-fetching unnecessarily

    // Select a time range without closing the dropdown
    const selectTimeRange = (timeRange) => {
        setSelectedTimeRange(timeRange);
        return false; // Prevent closing the dropdown
    };

    // Toggle a bin selection without closing the dropdown
    const toggleBinSelection = (bin) => {
        setSelectedBins(prev => {
            const newValue = prev.includes(bin)
                ? prev.filter(item => item !== bin)
                : [...prev, bin];
            console.log("Layout: Toggled bin selection, now have", newValue.length, "bins selected");
            return newValue;
        });
        return false; // Prevent closing the dropdown
    };

    // Select a year without closing the dropdown
    const selectYear = (year) => {
        setSelectedYear(year);
        return false; // Prevent closing the dropdown
    };

    // Toggle a waste type selection without closing the dropdown
    const toggleWasteTypeSelection = (wasteType) => {
        setSelectedWasteTypes(prev => {
            const newValue = prev.includes(wasteType)
                ? prev.filter(item => item !== wasteType)
                : [...prev, wasteType];
            return newValue;
        });
        return false; // Prevent closing the dropdown
    };

    // Handler for cost savings waste type selection
    const selectWaste = (waste) => {
        setSelectedWaste(waste);
    };

    // NEW: Handler for custom date range changes
    const handleCustomDateRangeChange = (dateRange, isCustom) => {
        console.log('Layout: Date range changed', { dateRange, isCustom });
        setCustomDateRange(dateRange);
        setIsCustomDateRange(isCustom);
        
        // If resetting to time range (not custom), reset to default time range
        if (!isCustom) {
            setSelectedTimeRange(timeRangeOptions[2]); // Default to "Month"
        }
    };

    // Common header props used by any header component
    const headerProps = {
        timeRangeDropdownOpen,
        setTimeRangeDropdownOpen,
        binDropdownOpen,
        setBinDropdownOpen,
        selectedTimeRange,
        selectTimeRange,
        timeRangeOptions,
        selectedBins,
        allBins,
        toggleBinSelection,
        yearDropdownOpen,
        setYearDropdownOpen,
        selectedYear,
        selectYear,
        yearOptions,
        wasteTypeDropdownOpen,
        setWasteTypeDropdownOpen,
        selectedWasteTypes,
        toggleWasteTypeSelection,
        wasteTypeOptions,
        // Cost savings specific props
        selectedWaste,
        setSelectedWaste: selectWaste,
        // NEW: Custom date range props
        customDateRange,
        onCustomDateRangeChange: handleCustomDateRangeChange,
        isCustomDateRange,
    };

    return (
        <SidebarProvider>
            <AppSidebar variant="inset" userRole={userRole}/>
            <SidebarInset>
                {/* We'll still conditionally render the main SiteHeader, but provide headerProps to the Outlet */}
                {location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <SiteHeader {...headerProps} />
                )}

                {/* Spacer between header and content */}
                {!location.pathname.includes('/account') && location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <div className="h-4 md:h-6"></div>
                )}

                <div className="tab-content h-full">
                    <Outlet context={{
                        ...headerProps,
                        selectedTimeRange,
                        selectedBins,
                        selectedYear,
                        selectedWasteTypes,
                        selectedWaste,
                        userRole,
                        // NEW: Pass custom date range through context
                        customDateRange,
                        isCustomDateRange,
                    }}/>
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
};

export default SidebarLayout;

// Helper hook to access the filter values
export function useFilters() {
    try {
        return useOutletContext() || {
            selectedTimeRange: null,
            selectedBins: null,
            selectedYear: null,
            selectedWasteTypes: null,
            selectedWaste: null,
            userRole: null,
            customDateRange: null,
            isCustomDateRange: false,
        };
    } catch (error) {
        return {
            selectedTimeRange: null,
            selectedBins: null,
            selectedYear: null,
            selectedWasteTypes: null,
            selectedWaste: null,
            userRole: null,
            customDateRange: null,
            isCustomDateRange: false,
        };
    }
}